// ════════════════════════════════════════════════════════════════════════════
// THE research-address module — the two address families a search answer
// prints, and everything that composes or reads one.
//
//   `/r/<token>`         a record with no destination of its own. The token IS
//                        the ref grammar's body — R9 (2026-08-20), verbatim:
//                        *"the link should be the ref itself, we should not
//                        have another var only links."*
//   `/l/<host>/<code>`   a record whose destination is a real page. The code
//                        is the RECORD FINGERPRINT alone — decision 7
//                        (2026-08-22), verbatim: *"the '~' is super non
//                        standard and shouldn't exist in our minted sources.
//                        I'd remove the search params and resolve to
//                        /l/booking.com/kQ2fB7xR."*
//
// Neither family carries a second variable. The `/r/` token is the ref itself;
// the `/l/` code is derived from the record's own facts, stored nowhere on the
// record, and read back by RECOMPUTING it over the reader's live store —
// exactly the recompute the print-coherence audit already runs. A ref is
// scoped to one member's live store while a published link is global and
// permanent, and the fingerprint is what carries a global string back to the
// one record that produces it. See the citation-codes section comment for what
// that buys and what it refuses.
//
// ─── the `/r/` action-address family ────────────────────────────────────────
//
// Ruled 2026-08-16 (planning/research-layer/serpers/pipeline/
// FOLLOWUP-URL-SURFACE.md, rulings U5/U6). A search answer prints ONE link per
// row and one search-level offer line; both are addresses, and an address is
// the only thing a researcher ever sends back.
//
//   <origin>/r/<token>[?page=N|?term=phrase]
//
//   <origin>/r/WBHL-5                search-level address (next unserved page)
//   <origin>/r/WBHL-5?page=2         an ordinal jump
//   <origin>/r/HOGO-12-3             one row's address
//   <origin>/r/HIIA-12-1?term=religious tolerance
//
// STORED NOWHERE (U6). There is no table, no code, no row: `<token>` IS the
// ref grammar body from ./follow-up-refs.js, so an address resolves only
// against the calling member's live ref store, and only inside the tool that
// receives it. The HTTP route serving `/r/*` therefore looks nothing up.
//
// This sits beside the grammar rather than beside the short-link URL helpers
// because the token half is grammar, not URL: every parse here delegates to
// `parseRef`, which is what keeps a printed address parseable by construction
// and — the load-bearing consequence — what makes a foreign `/r/` path safe to
// ignore. `reddit.com/r/programming` is not an action address, and nothing
// here has to know about reddit to answer that: `programming` is not the
// grammar.
//
// Pure and origin-agnostic on the way in, exactly like `parseShortLinkCode`:
// composition takes the origin as an argument, and reading matches the path
// shape rather than today's host, so an address minted before an origin change
// still reads.
// ════════════════════════════════════════════════════════════════════════════

import { shortLinkFingerprint } from
  '../../../../business-logic/domains/short-link/short-link.url.js';
import {
  FollowUpAction,
  RES_REF_PREFIX,
  parsePositiveSafeInt,
  parseRef,
  type FollowUpRefAction,
  type ParsedFollowUpRef,
} from './follow-up-refs.js';
import type { RowRefRecord } from './types.js';

/** The root-level path segment the `/r/` route mounts on. */
export const RESEARCH_ADDRESS_PATH_SEGMENT = 'r';

// The three filters an address can carry. Ruled mapping from the fragment
// vocabulary the grammar already owns (URL-SURFACE §2; `include` ruled
// 2026-08-22, decision 3 — "?include=comments,stats … multiple items are
// allowed"):
//
//   #more            → the bare address on a More-only row (bare still
//                      continues there) and on a search (the next unserved
//                      page); `?include=more` on a drill-bearing row, whose
//                      bare address stays the drill
//   #page=N          → ?page=N
//   #search=phrase   → ?term=phrase
//   #details         → the bare address
//   #transcript      → ?include=transcript
//   #comments        → ?include=comments
//   #stats           → ?include=stats
//   #citations       → ?include=citations
//   #references      → ?include=references
//
// `term` rather than `search` because the address IS a search; a `?search=`
// on it would name the noun twice and read as "which search", not "what to
// look for inside it".
export enum ActionLinkParam {
  Page = 'page',
  Term = 'term',
  Include = 'include',
}

// The act names `?include=` accepts — every act name, ruled 2026-08-23:
// `more, details, transcript, comments, stats, citations, references`.
// `page` and `term` stay separate params and are never include values: they
// are the two VALUED asks, and a value has its own key.
export type IncludableAction = Exclude<
  FollowUpAction,
  FollowUpAction.Page | FollowUpAction.Search
>;

// Canonical order for the include list — the enum's own declaration order,
// which is what `buildActionLink` serializes so one act set is one string.
const INCLUDE_ACTS: readonly IncludableAction[] = Object.values(FollowUpAction)
  .filter((action): action is IncludableAction =>
    action !== FollowUpAction.Page && action !== FollowUpAction.Search);

const INCLUDE_ACT_SET: ReadonlySet<string> = new Set(INCLUDE_ACTS);

// What an address asks for beyond the record itself. `null` is the bare
// address — the whole of `#more` and `#details`, which differ only in what the
// resolved record can do, never in what was typed.
//
// The Include variant is the one PLURAL ask: a comma-separated act list, each
// act dispatched through the record's own executor and every unsupported one
// folded by name (ruled [B]/[C], 2026-08-23). A `?page=`/`?term=` arriving
// BESIDE the list rides the variant rather than applying on its own — whether
// an included act can take it is the record's fact, so the dispatcher is the
// seam that decides, and an unhonoured key is named there.
export type ActionLinkFilter =
  | { readonly param: ActionLinkParam.Page; readonly page: number }
  | { readonly param: ActionLinkParam.Term; readonly term: string }
  | {
      readonly param: ActionLinkParam.Include;
      readonly acts: readonly IncludableAction[];
      readonly page?: number;
      readonly term?: string;
    };

export interface BuildActionLinkInput {
  readonly origin: string;
  // The canonical ref the record carries (`res-ref-HOGO-12-3`). The prefix is
  // stripped here, in one place, so no caller ever hand-slices an address.
  readonly ref: string;
  readonly filter?: ActionLinkFilter;
}

// The grammar body of a canonical ref — `res-ref-HOGO-12-3` → `HOGO-12-3`.
// Throws rather than degrading: every caller is our own code holding a record
// the store minted, and an address built from a non-ref would print a string
// that resolves nowhere.
export function actionTokenOf(input: { ref: string }): string {
  if (!input.ref.startsWith(RES_REF_PREFIX)) {
    throw new Error(`"${input.ref}" is not a result ref, so it has no action token`);
  }
  return input.ref.slice(RES_REF_PREFIX.length);
}

const INCLUDE_VALUE_SEPARATOR = ',';

// THE composer for every printed action address — one home, so the printed and
// the parsed form cannot drift (D12).
export function buildActionLink(input: BuildActionLinkInput): string {
  const token = actionTokenOf({ ref: input.ref });
  const base = `${input.origin}/${RESEARCH_ADDRESS_PATH_SEGMENT}/${token}`;
  if (input.filter === undefined) return base;
  if (input.filter.param === ActionLinkParam.Page) {
    return `${base}?${ActionLinkParam.Page}=${input.filter.page}`;
  }
  if (input.filter.param === ActionLinkParam.Term) {
    return `${base}?${ActionLinkParam.Term}=${encodeURIComponent(input.filter.term)}`;
  }
  // The include list serializes deduped, in the enum's own order, so one act
  // set composes one string however a caller assembled it. Composing an empty
  // list would print `?include=` — a key the reader is then told was ignored —
  // and every caller is our own code, so it throws like `actionTokenOf`.
  const filter = input.filter;
  const acts = INCLUDE_ACTS.filter((act) => filter.acts.includes(act));
  if (acts.length === 0) {
    throw new Error(`an include address for ${input.ref} names no act`);
  }
  const page = filter.page === undefined
    ? ''
    : `&${ActionLinkParam.Page}=${filter.page}`;
  const term = filter.term === undefined
    ? ''
    : `&${ActionLinkParam.Term}=${encodeURIComponent(filter.term)}`;
  return `${base}?${ActionLinkParam.Include}=${acts.join(INCLUDE_VALUE_SEPARATOR)}${page}${term}`;
}

// The fragment vocabulary translated to the URL one. A `#more` or `#details`
// is the bare address, so both answer `undefined` — the two are the same typed
// string, and which one a record honours is the record's to decide. (On a
// drill-bearing row `#more` must be TYPED as `?include=more`, but which rows
// those are is the record's fact — this translation has no record, so the
// bare mapping stands and the print sites that know pass the spelling.)
// A NAMED drill translates to its `?include=` spelling — the ruled typeable
// form of the acts a bare address cannot select.
export function actionLinkFilterOf(input: {
  action: FollowUpRefAction;
}): ActionLinkFilter | undefined {
  if (input.action.kind === FollowUpAction.Page) {
    return { param: ActionLinkParam.Page, page: input.action.page };
  }
  if (input.action.kind === FollowUpAction.Search) {
    return { param: ActionLinkParam.Term, term: input.action.phrase };
  }
  if (
    input.action.kind === FollowUpAction.More
    || input.action.kind === FollowUpAction.Details
  ) {
    return undefined;
  }
  return { param: ActionLinkParam.Include, acts: [input.action.kind] };
}

// ─── Reading ────────────────────────────────────────────────────────────────

// Why reading never throws: an address is a MODEL input, and an unreadable one
// becomes a typed refusal at the tool. Same stance as `parseRef`.
export enum ActionLinkParseFailureKind {
  // The URL is not an action address at all: wrong shape, wrong segment count,
  // or a `/r/` path belonging to somebody else entirely.
  NotAnActionLink = 'not_an_action_link',
  // The `/r/` path is there but its token is not the grammar. This is the
  // branch that keeps `reddit.com/r/programming` somebody else's page: the path
  // shape matches and the token does not, so the address is not ours and the
  // caller reads it as the page it is.
  MalformedToken = 'malformed_token',
}

export interface ActionLinkParseFailure {
  readonly ok: false;
  readonly kind: ActionLinkParseFailureKind;
  readonly input: string;
}

export interface ParsedActionLink {
  readonly ok: true;
  // The canonical ref the token stands for, ready to resolve against a store.
  readonly ref: string;
  readonly parsedRef: ParsedFollowUpRef;
  readonly filter: ActionLinkFilter | null;
  // Params the address carried that this grammar has no reading for. Ruled U8:
  // they are ignored, and the answer SAYS which — a fold nobody is told about
  // is a defect, so they are carried out rather than dropped here.
  //
  // "No reading" is one rule, not two: a key outside the vocabulary and a
  // RECOGNIZED key whose value the vocabulary cannot hold (`?page=0`, an empty
  // `?term=`, `page` and `term` sent at once) both end here, because both leave
  // the reader with an answer that did nothing about what it asked. Before
  // this, the second kind failed the whole parse, `actionLinkRefOf` answered
  // null, and `fetch` read the address as a web page — so an address the
  // grammar minted came back as CABAL's own `/r/` explainer instead of a
  // record (measured: `r11-printed-reach-*.json`).
  //
  // Named in the bytes the ADDRESS carried, not in their decoded form, and that
  // is load-bearing rather than fussy: `searchParams` decodes, so `?a%0A1.%20x`
  // becomes a key holding a real line break and a `1. ` opening — and a key is
  // model-supplied text on its way into a research answer whose rows are found
  // by their own line grammar. Measured, not reasoned: `?a%0A1.%20fake=1` reads
  // back as "a\n1. fake" through `keys()`, while the raw query is single-line by
  // construction (a raw newline never survives `new URL` at all). Every ordinary
  // key is identical in both forms.
  readonly ignoredParams: readonly string[];
}

export type ActionLinkParseResult = ParsedActionLink | ActionLinkParseFailure;

const HTTP_PROTOCOLS: readonly string[] = ['http:', 'https:'];
const ACTION_LINK_PATH_SEGMENTS = 2;

// The path-shape read: `/r/<token>` and nothing after it. Returns the raw
// token, or null when this URL is not an action address by shape.
function actionTokenIn(input: { url: string }): string | null {
  let parsed: URL;
  try {
    parsed = new URL(input.url);
  } catch {
    return null;
  }
  if (!HTTP_PROTOCOLS.includes(parsed.protocol)) return null;
  const segments = parsed.pathname.split('/').filter((segment) => segment.length > 0);
  if (segments.length !== ACTION_LINK_PATH_SEGMENTS) return null;
  if (segments[0] !== RESEARCH_ADDRESS_PATH_SEGMENT) return null;
  // `%E0%A4%A` is a `URIError` out of `decodeURIComponent`, and this runs over
  // text a model wrote.
  try {
    return decodeURIComponent(segments[1]);
  } catch {
    return null;
  }
}

export function parseActionLink(input: { url: string }): ActionLinkParseResult {
  const token = actionTokenIn({ url: input.url });
  if (token === null) {
    return {
      ok: false,
      kind: ActionLinkParseFailureKind.NotAnActionLink,
      input: input.url,
    };
  }
  // The grammar module is the single authority on the token half: an address
  // is exactly a ref wearing a URL, so `res-ref-` + token must parse.
  const ref = `${RES_REF_PREFIX}${token}`;
  const parsedRef = parseRef(ref);
  if (!parsedRef.ok) {
    return {
      ok: false,
      kind: ActionLinkParseFailureKind.MalformedToken,
      input: input.url,
    };
  }
  const query = readActionLinkQuery({ url: input.url });
  return {
    ok: true,
    ref,
    parsedRef,
    filter: query.filter,
    ignoredParams: query.ignoredParams,
  };
}

/**
 * What an address's QUERY asks for, and what it asked that went unanswered.
 *
 * The query half of the grammar, read on its own because it is the half that
 * does not care which clothes the address wears. A record is named by
 * `/r/<token>` or by a `/l/` citation code (R9), and in both cases `?page=`
 * and `?term=` mean the same thing about the record that was named — so the
 * reading lives here once, and the two coats differ only in where the ref
 * comes from.
 *
 * ONE RULE DECIDES BOTH FIELDS: a filter is applied only when exactly one
 * recognized key is present AND its value is one the vocabulary can hold.
 * Anything else applies nothing, and then every key the address carried is
 * named as unread — including a recognized one. That is what keeps `?page=0`
 * and `?page=2&term=x` answerable at all: honouring one of two asks silently
 * is the fold this surface exists to refuse, and refusing the whole address
 * over it costs the reader an answer it can have.
 *
 * THE ONE PLURAL EXCEPTION is `?include=` with at least one value (ruled
 * 2026-08-23): the include filter applies, and a holdable `?page=`/`?term=`
 * beside it RIDES the filter instead of counting as a second ask — whether an
 * included act can take it is the record's fact, so the dispatcher decides and
 * names what it folds. An unholdable value beside include, an unknown include
 * value, and a bare `?include=` are all named here, exactly like every other
 * key nothing read. A hand-typed `#fragment` outranks the whole query: it IS
 * the ask, no filter applies, and every query key is named.
 */
export interface ActionLinkQuery {
  readonly filter: ActionLinkFilter | null;
  readonly ignoredParams: readonly string[];
}

export function readActionLinkQuery(input: { url: string }): ActionLinkQuery {
  let parsed: URL;
  try {
    parsed = new URL(input.url);
  } catch {
    return { filter: null, ignoredParams: [] };
  }
  // A hand-typed fragment IS the ask (`askedRefOf`: fragment first), so an
  // address carrying one applies no query filter — and every recognized key it
  // also carried is then a key nothing read, named like any other (ruled [B]:
  // every unhonoured key is named; before this a `?page=2` beside a typed
  // fragment folded silently).
  if (parsed.hash.length > 0) {
    return {
      filter: null,
      ignoredParams: unreadKeysIn({ query: parsed.search, applied: NO_KEYS_APPLIED }),
    };
  }
  const include = includeValuesIn({ query: parsed.search });
  if (include === null) {
    const filter = readFilter({
      page: parsed.searchParams.get(ActionLinkParam.Page),
      term: parsed.searchParams.get(ActionLinkParam.Term),
    });
    return {
      filter,
      ignoredParams: unreadKeysIn({
        query: parsed.search,
        applied: filter === null ? NO_KEYS_APPLIED : new Set([filter.param as string]),
      }),
    };
  }
  if (!include.asked) {
    // `?include=` with no value asks for nothing — the key is named and the
    // bare-address act serves, exactly like any other recognized key whose
    // value the vocabulary cannot hold. Its PRESENCE still counts as a
    // recognized key, so `?include=&page=2` applies nothing and names both
    // (one rule decides both fields — see the interface comment).
    return {
      filter: null,
      ignoredParams: unreadKeysIn({ query: parsed.search, applied: NO_KEYS_APPLIED }),
    };
  }
  // The include ask. A `?page=`/`?term=` beside it rides the filter when its
  // value is one the vocabulary can hold — whether an included act can TAKE it
  // is the record's fact, decided at dispatch — while an unholdable value is
  // named here, because no record could have honoured it under any act.
  const pageRaw = parsed.searchParams.get(ActionLinkParam.Page);
  const page = pageRaw === null ? null : parsePositiveSafeInt(pageRaw);
  const termRaw = parsed.searchParams.get(ActionLinkParam.Term);
  const term = termRaw !== null && termRaw.trim().length > 0 ? termRaw : null;
  const applied = new Set<string>([
    ActionLinkParam.Include,
    ...(page === null ? [] : [ActionLinkParam.Page as string]),
    ...(term === null ? [] : [ActionLinkParam.Term as string]),
  ]);
  return {
    filter: {
      param: ActionLinkParam.Include,
      acts: include.acts,
      ...(page === null ? {} : { page }),
      ...(term === null ? {} : { term }),
    },
    ignoredParams: [...new Set([
      ...unreadKeysIn({ query: parsed.search, applied }),
      ...include.unknownValues,
    ])],
  };
}

const QUERY_LEAD = '?';
const QUERY_PAIR_SEPARATOR = '&';
const QUERY_VALUE_SEPARATOR = '=';
const QUERY_PLUS = /\+/gu;
const QUERY_SPACE = ' ';

// Nothing applied: every key the address carried is named.
const NO_KEYS_APPLIED: ReadonlySet<string> = new Set();

// Every query key the address carried that was not one APPLIED, in the
// address's own bytes, deduplicated in the order they were sent.
//
// Keyed on what was applied rather than on what is recognized, and that is the
// whole difference: `page` on `?page=2` was read, `page` on `?page=0` was not,
// and the reader deserves the same answer about both — what did this address
// do. An empty set means nothing was applied, so every key is named.
//
// The comparison decodes and the answer does not, and the two halves must not
// drift: `?%70age=2` IS `page` to `searchParams`, so a raw-key comparison would
// apply the filter AND report it unread, which is a claim the arrival disproves
// (D22). Decoding follows the same form rule `URLSearchParams` follows — `+` is
// a space — so the applied key is read exactly once, the same way twice.
//
// A key with nothing in it (`?=1`) is dropped: there is no name to state.
function unreadKeysIn(input: {
  query: string;
  applied: ReadonlySet<string>;
}): readonly string[] {
  if (!input.query.startsWith(QUERY_LEAD)) return [];
  const unread = input.query
    .slice(QUERY_LEAD.length)
    .split(QUERY_PAIR_SEPARATOR)
    .map((pair) => pair.split(QUERY_VALUE_SEPARATOR)[0] ?? '')
    .filter((key) => key.length > 0 && !input.applied.has(decodedKeyOf({ key })));
  return [...new Set(unread)];
}

// How the include VALUE splits into act names. The raw bytes split on the
// literal comma and on its escape, so `a,b` and `a%2Cb` read identically —
// `searchParams` would decode `%2C` into the same comma, and reading the raw
// value (which the unknown-value naming below requires) must not read less.
const INCLUDE_RAW_SPLIT = /(?:,|%2[Cc])/u;

// What the `?include=` key asked for, or null when the address never carried
// the key.
//
//   `asked: false`  the key arrived with no value at all (`?include=`) — a
//                   recognized key holding nothing, named by the caller.
//   `asked: true`   at least one value arrived. `acts` is the known act names,
//                   deduped in send order, matched case-insensitively against
//                   the enum's own values; `unknownValues` names every piece
//                   the vocabulary has no reading for, in the address's OWN
//                   bytes under its key (`include=zebra`) — the same
//                   injection stance as the unread keys above, because an
//                   include value is model-supplied text on its way into an
//                   answer whose rows are found by their own line grammar.
type IncludeValuesReading =
  | { readonly asked: false }
  | {
      readonly asked: true;
      readonly acts: readonly IncludableAction[];
      readonly unknownValues: readonly string[];
    };

function includeValuesIn(input: { query: string }): IncludeValuesReading | null {
  if (!input.query.startsWith(QUERY_LEAD)) return null;
  const rawValues = input.query
    .slice(QUERY_LEAD.length)
    .split(QUERY_PAIR_SEPARATOR)
    .flatMap((pair) => {
      const valueAt = pair.indexOf(QUERY_VALUE_SEPARATOR);
      const key = valueAt === -1 ? pair : pair.slice(0, valueAt);
      if (decodedKeyOf({ key }) !== (ActionLinkParam.Include as string)) return [];
      return [valueAt === -1 ? '' : pair.slice(valueAt + 1)];
    });
  if (rawValues.length === 0) return null;
  const pieces = rawValues.flatMap((value) =>
    value.split(INCLUDE_RAW_SPLIT).filter((piece) => piece.length > 0));
  if (rawValues.every((value) => value.length === 0)) return { asked: false };
  const acts: IncludableAction[] = [];
  const unknownValues: string[] = [];
  pieces.forEach((piece) => {
    // Values parse case-insensitively, decoded the way the keys are decoded.
    const named = decodedKeyOf({ key: piece }).trim().toLowerCase();
    if (INCLUDE_ACT_SET.has(named)) {
      const act = named as IncludableAction;
      if (!acts.includes(act)) acts.push(act);
      return;
    }
    const unknown = `${ActionLinkParam.Include}${QUERY_VALUE_SEPARATOR}${piece}`;
    if (!unknownValues.includes(unknown)) unknownValues.push(unknown);
  });
  return { asked: true, acts, unknownValues };
}

// A raw key read the way the recognized set was read. An unreadable escape is
// not a key this grammar could have honoured under any spelling, so it answers
// itself and is reported unread.
function decodedKeyOf(input: { key: string }): string {
  try {
    return decodeURIComponent(input.key.replace(QUERY_PLUS, QUERY_SPACE));
  } catch {
    return input.key;
  }
}

// The one filter an address applies, or null for every arrival that applies
// none. Null covers four arrivals and treats them alike, because they are alike
// to the reader: no recognized key at all; both keys at once (two asks, not a
// bigger one); a page number the number type would round, which is a jump to a
// page nobody asked for; and an empty phrase, which is not a phrase.
//
// Nothing here refuses. What went unapplied is named by `unreadKeysIn` and
// stated in the answer (ruling U8), so a value this grammar cannot hold costs
// the reader a filter and never the record.
function readFilter(input: {
  page: string | null;
  term: string | null;
}): ActionLinkFilter | null {
  if (input.page !== null && input.term !== null) return null;
  if (input.page !== null) {
    const page = parsePositiveSafeInt(input.page);
    return page === null ? null : { param: ActionLinkParam.Page, page };
  }
  if (input.term !== null) {
    return input.term.trim().length === 0
      ? null
      : { param: ActionLinkParam.Term, term: input.term };
  }
  return null;
}

// ─── Citation codes — the `/l/` family's token (decision 7, 2026-08-22) ─────
//
// A row whose result has a real destination prints a citation link, and its
// code is the fingerprint of the RECORD, alone:
//
//   res-ref-HOGO-12-3  →  https://…/l/booking.com/kQ2fB7xR
//
// The ruling, verbatim: *"the '~' is super non standard and shouldn't exist in
// our minted sources. I'd remove the search params and resolve to
// /l/booking.com/kQ2fB7xR."* The earlier shape carried the ref token and the
// fingerprint joined by `~`; the ref segment and the separator are both gone
// from every minted source, and the old `token~print` strings are DEAD — the
// short-link parser rejects them, so they read as pages (greenfield law: no
// compat branch, no migration).
//
// WHY THE CODE CAN BE THE FINGERPRINT ALONE. A ref is member-scoped runtime
// state (U1): every store allocates its own search sequence from 1, so
// `HOGO-1-3` exists in every attempt of every member of every session and
// means a different row in each. A published link is the opposite — global,
// permanent, clicked by humans through a route that has no session and no
// member. So the ref never belonged in the published string; what the string
// has to name is the RECORD, and the fingerprint is a pure function of facts
// the record ALREADY carries — its ref, the instant it was captured, the raw
// address it was minted from, and its payload. Consequences:
//
//   · nothing new is stored on the record, in the store, or in any table;
//   · the printed link is composed from the record, and read back by
//     RECOMPUTING the fingerprint over the reader's own store — snapshot the
//     rows once per call, recompute per record, match. No lookup, no binding
//     pass, and the same recompute the print-coherence audit runs;
//   · a link belonging to another member, another attempt or another session
//     cannot answer against this store, because no record here produces its
//     fingerprint — which is what keeps U15 true with no ref in the string;
//   · two records colliding requires the same ref captured in the same
//     millisecond from the same url with a byte-identical payload — which is
//     the same record. Table-wide, across records that share none of that,
//     the guard is the fingerprint width (see `CODE_FINGERPRINT_CHARS`).
//
// What a code no longer carries, resolution no longer reads: the citation
// branch of `fetch` and of the attach resolver match by recomputed
// fingerprint, and a code no store record answers for falls to the short-link
// table and the page ladder, exactly as before.

/**
 * The fingerprint of the record a ref names — the WHOLE citation code
 * (decision 7) — or null when the record cannot carry a citation link at all:
 * no destination to shorten, no capture instant to date it by (U16), or a
 * payload that will not serialize. Null is the honest answer, not a failure:
 * such a row prints its `/r/` address instead.
 *
 * FOUR INPUTS, and each of them is load-bearing against a real collision:
 *
 *   ref         two rows of one store are two records.
 *   capturedAt  two stores' row 1 of search 1 are two records — every store
 *               numbers its searches from 1, so the ref alone does not separate
 *               a member from a teammate.
 *   url         a record's destination is part of what it IS.
 *   payload     the millisecond is not fine enough to separate two members who
 *               searched concurrently. What separates them then is the ANSWER:
 *               the same hotel priced for two different stays is two payloads,
 *               and §3.3's stay-dates hazard crossing the member boundary is
 *               exactly what U15 forbids.
 *
 * What remains indistinguishable is two records agreeing on all four — same
 * ref, same instant, same destination, same answer. Those are one record for
 * every purpose a link serves: resolving to either returns the same data.
 */
function recordFingerprintOf(input: { row: RowRefRecord }): string | null {
  const url = input.row.address.url;
  const capturedAt = input.row.capturedAt;
  if (url === undefined || capturedAt === undefined) return null;
  const payload = serializedPayloadOf({ payload: input.row.payload });
  if (payload === null) return null;
  return shortLinkFingerprint(
    [input.row.ref, capturedAt, url, payload].join('\n'),
  );
}

// Records are JSON-like by contract (they ride a jsonb column) and the store
// clones them in and out, so a record serializes to the same string every time
// it is read. One that does not is a record no citation link can name — and
// answering null leaves the row printing its `/r/` address, which is the same
// degradation a row with no destination takes.
function serializedPayloadOf(input: { payload: unknown }): string | null {
  try {
    return JSON.stringify(input.payload) ?? '';
  } catch {
    return null;
  }
}

/**
 * The code this row's citation link is published under — the record
 * fingerprint, alone (decision 7) — or null when the row has none.
 *
 * THE one composer (D12). The mint seam calls it to publish, every reader calls
 * it to recognise, and the print↔resolve audit calls it to check — so a printed
 * string and a read string cannot drift, because there is only one string.
 *
 * The ref-grammar guard is not decoration: a record whose ref is not the
 * grammar is not one the store minted, and publishing a code for it would
 * print a link `rowForCitationCode` can find but no `/r/` twin could ever
 * name.
 */
export function citationCodeOf(input: { row: RowRefRecord }): string | null {
  if (!parseRef(input.row.ref).ok) return null;
  return recordFingerprintOf({ row: input.row });
}

/**
 * The row a printed citation code stands for, or null.
 *
 * THE one reading, and it is a RECOMPUTED-FINGERPRINT MATCH (decision 7): the
 * code carries no ref any more, so nothing is parsed out of it — the caller
 * snapshots its store's rows once, and each record's code is recomputed and
 * compared. The fingerprint is the ownership proof: a link minted in another
 * member's store answers null here, because no record of THIS store produces
 * its fingerprint — that store's rows were captured at other instants from
 * other addresses with other payloads (U15).
 *
 * Null is also the answer for a plain vendor url's code, an old `token~print`
 * code, and any `/l/` string from somewhere else: none of them is a
 * fingerprint any record here produces, and all of them are a page — the
 * cascade's table-then-ladder branch (U12).
 *
 * AMBIGUITY IS REFUSED, never resolved by position. Two DISTINCT records
 * reproducing one code is a 48-bit digest accident — the four fingerprint
 * inputs agree only on the same record, but the digest over them can
 * accidentally agree across records — and `.find` would silently open
 * whichever record the store returned first, which is the wrong record for
 * at least one of the two printings. A code more than one record answers for
 * therefore matches NO row: resolution falls to the short-link table and the
 * page ladder, whose stored destination is the one the code was actually
 * published under (the mint seam refuses a same-code/different-URL claim),
 * so the reader gets the correct page instead of a coin-flip record.
 */
export function rowForCitationCode(input: {
  rows: readonly RowRefRecord[];
  code: string;
}): RowRefRecord | null {
  const matches = input.rows.filter((row) => citationCodeOf({ row }) === input.code);
  return matches.length === 1 ? matches[0] ?? null : null;
}

// ─── Leak detection ─────────────────────────────────────────────────────────

// The action-address twin of `findResRefMentions` (ruling U9: these addresses
// are blocked on every model-written surface, durable and transient, because a
// member-scoped runtime address written into anything that outlives the
// attempt is dead on arrival).
//
// Anchored on the GRAMMAR, not on our origin. That is what makes it safe to
// scan every string a model writes: `reddit.com/r/programming`,
// `example.org/r/2024`, a docs path `/r/getting-started` — none of them carry a
// token the ref grammar accepts, so none of them is flagged, and a citation
// link to a subreddit keeps working. Conversely an address survives an origin
// change, a scheme change, and a markdown wrapper, because none of those is
// what is being matched.
//
// The dress forms the ruling names are covered by matching the URL span itself:
//   bare      `https://cabal-ai.io/r/WBHL-5?page=2`
//   framed    `[page 2](https://cabal-ai.io/r/WBHL-5?page=2)`, `<https://…>`,
//             `(https://…)`, `"https://…"`
// plus the SCHEME-LESS mangle a model produces by trimming — `cabal-ai.io/r/
// WBHL-5` is the same dangling address and fails the same way, exactly as the
// res-ref detector fails anything wearing its prefix as an address — and the
// ROOTLESS mangle one more trim produces, `/r/WBHL-5`, where the whole host is
// gone and the address starts at the path (phase-4 validation, required fix 2:
// the bare path form escaped this scan). The rootless dress keeps the same
// safety anchor as every other one: `/r/getting-started` carries no grammar
// token and stays somebody's docs path.
// The reported mention is the ADDRESS SPAN ONLY — the trailing `)`, `>`, quote
// or sentence punctuation is not part of the name.
// Markdown and prose framing characters are excluded on BOTH sides, not just
// the closing side: without a scheme to anchor on, an opening `(` or `[` would
// otherwise be eaten into the span and the whole address would read as
// unparseable.
const ADDRESS_CHARACTER = `[^\\s<>"'\`\\[\\](),]`;
const ACTION_ADDRESS_SCAN = new RegExp(
  `(?:(?:https?://)?${ADDRESS_CHARACTER}+\\.${ADDRESS_CHARACTER}*?)?/${RESEARCH_ADDRESS_PATH_SEGMENT}/${ADDRESS_CHARACTER}+`,
  'gi',
);
const HTTPS_SCHEME = 'https://';
const SCHEME_PATTERN = /^https?:\/\//i;
const TRAILING_PUNCTUATION = /[.;:!?]+$/;
// A rootless span has no host to lend the URL parser. Any host satisfies the
// ONE reading — `parseActionLink` judges protocol, path shape and token, never
// the host — so a placeholder that can never be a real destination stands in.
const ROOTLESS_PARSE_ORIGIN = 'https://origin.invalid';

export function findActionAddressMentions(text: string): string[] {
  const mentions = [...text.matchAll(ACTION_ADDRESS_SCAN)].flatMap((match) => {
    // Trailing sentence punctuation belongs to the prose, never to the
    // address: `see …/r/WBHL-5.` names `…/r/WBHL-5`.
    const candidate = match[0].replace(TRAILING_PUNCTUATION, '');
    // A scheme-less span is read as https — and a rootless one under the
    // placeholder origin — so the ONE parser decides; the reported mention
    // stays what the writer actually typed.
    const readable = SCHEME_PATTERN.test(candidate)
      ? candidate
      : candidate.startsWith('/')
        ? `${ROOTLESS_PARSE_ORIGIN}${candidate}`
        : `${HTTPS_SCHEME}${candidate}`;
    // The token is what decides ours from somebody else's `/r/` path, and a
    // query never unmakes an address: an arrival's params are answered rather
    // than refused, so every well-formed token parses and every mention of one
    // is reported however it was filtered.
    return parseActionLink({ url: readable }).ok ? [candidate] : [];
  });
  return [...new Set(mentions)];
}

// ─── the link-position reading (phase-4 validation, required fix 2) ─────────
//
// A string OFFERED AS A LINK — an attach block's `links:` value, where every
// item is an address by the writer's own declaration — wears one dress free
// text never carries: the NAKED grammar token itself (`HOGO-99-1`, `WBHL-5`),
// with or without the filters an address may carry (`HOGO-99-1?page=2`,
// `WBHL-5#more`). In prose that spelling stays prose — `the TR-808 drum
// machine` names an instrument, not a search, which is exactly why
// `findActionAddressMentions` must never read naked tokens out of free text.
// In link position the writer has said "this string is an address", and the
// ref grammar alone decides whose it is.
//
// Defined HERE, beside the free-text reading, so the attach plan seam and the
// research-address guard consult ONE grammar in one home — never a second
// classifier. The filter tail is set aside before the token is judged (the
// same stance as the URL reading: a query or fragment never unmakes an
// address), so a mangled filter on a real token still names the address.
const FILTER_TAIL_OPENER = /[?#]/;

/** The offered string, trimmed, when it is an action address in ANY dress —
 *  hosted URL, scheme-less mangle, rootless `/r/<token>` path, or the naked
 *  grammar token — and null when it is somebody else's link. */
export function offeredLinkActionAddress(input: { url: string }): string | null {
  const candidate = input.url.trim();
  if (candidate.length === 0) return null;
  if (findActionAddressMentions(candidate).length > 0) return candidate;
  const body = candidate.split(FILTER_TAIL_OPENER, 1)[0] ?? '';
  if (body.length === 0) return null;
  return parseRef(`${RES_REF_PREFIX}${body}`).ok ? candidate : null;
}
